import { useEffect, useState } from 'react';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import type { LyricsCandidate } from '@aeon-karaoke/cloud-convex/model/lrclib';
import { Button, Modal, MonoLabel, Textarea } from '@/component';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';
import { formatDuration } from '@/lib/time';

interface FixLyricsModalProps {
  trackId: Id<'tracks'>;
  /** "HUNTR/X · Golden", the song the track is matched to. */
  song: string;
  durationMs: number | undefined;
  onClose: () => void;
}

/** "1 s off": how far a candidate's length sits from this recording's. */
function offBy(candidate: LyricsCandidate, durationMs: number | undefined): string | null {
  if (durationMs === undefined || candidate.durationMs === null) return null;
  return `${Math.round(Math.abs(candidate.durationMs - durationMs) / 1000)} s off`;
}

/**
 * "Wrong lyrics?": LRCLIB's entries for the song, closest length first, and a
 * box to paste the words. Either one starts a relyric, which times the words
 * against the stored vocal; the owner never sets a timestamp.
 */
export function FixLyricsModal({ trackId, song, durationMs, onClose }: FixLyricsModalProps) {
  const [candidates, setCandidates] = useState<LyricsCandidate[] | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let open = true;
    void toResult(() => convex.action(api.tracks.lyricsCandidates, { trackId })).then((result) => {
      if (!open) return;
      if (result.ok) setCandidates(result.value);
      else setLookupError(result.error.message);
    });
    return () => {
      open = false;
    };
  }, [trackId]);

  async function rebuild(input: { lrclibId: number } | { text: string }) {
    setBusy(true);
    setError(null);
    const result = await toResult(() => convex.mutation(api.tracks.relyric, { trackId, ...input }));
    setBusy(false);
    if (result.ok) onClose();
    else setError(result.error.message);
  }

  return (
    <Modal open onClose={onClose} title="Which words are these?" kicker="Fix the words" maxWidth={560}>
      <p className="mt-3 max-w-[46ch] text-sm leading-[1.5] text-mute">
        Pick the published entry that matches this recording, or paste the words yourself. We time them against the
        vocal; you never set a timestamp.
      </p>
      <MonoLabel tone="mute" className="mt-5 block">
        LRCLIB · {song}
      </MonoLabel>
      {candidates === null && !lookupError && <p className="mt-3 text-sm text-mute">Looking up LRCLIB…</p>}
      {lookupError && <p className="mt-3 text-[13px] text-down">{lookupError}</p>}
      {candidates?.length === 0 && (
        <p className="mt-3 max-w-[46ch] text-sm text-mute">
          No published entries for {song}. Fix the match first with “Not this song?”, or paste the words.
        </p>
      )}
      {candidates?.map((candidate, index) => (
        <div
          key={candidate.lrclibId}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-[14px] border-b border-stripeA py-[11px]"
        >
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              {candidate.title} · {candidate.artist}
            </div>
            <div className="mt-[1px] truncate text-xs text-mute">
              {[candidate.album, candidate.durationMs !== null ? formatDuration(candidate.durationMs) : null]
                .filter(Boolean)
                .join(' · ')}
              {' · '}
              <MonoLabel tone={index === 0 ? 'up' : 'faint'}>
                {[candidate.synced ? 'synced' : 'plain', offBy(candidate, durationMs)].filter(Boolean).join(' · ')}
              </MonoLabel>
            </div>
          </div>
          <Button
            variant={index === 0 ? 'primary' : 'outline'}
            size="sm"
            disabled={busy}
            onClick={() => void rebuild({ lrclibId: candidate.lrclibId })}
            aria-label={`Use ${candidate.title} by ${candidate.artist}`}
          >
            Use
          </Button>
        </div>
      ))}
      <MonoLabel tone="mute" className="mt-6 block">
        Or paste the words
      </MonoLabel>
      <Textarea
        aria-label="Pasted words"
        rows={6}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="One line per sung line. Section tags like [Chorus] are fine; we drop them."
        className="mt-2 rounded-md border-line bg-bg text-[14px] text-ink placeholder:text-faint focus:border-line2 focus:ring-0"
      />
      <div className="mt-3 flex flex-wrap gap-[10px]">
        <Button onClick={() => void rebuild({ text })} disabled={busy || !text.trim()} className="px-[18px]">
          Time these words
        </Button>
        <Button variant="outline" quiet onClick={onClose} className="px-[17px]">
          Cancel
        </Button>
      </div>
      {error && <p className="mt-4 text-[13px] text-down">{error}</p>}
    </Modal>
  );
}
